// Sugalarm — Cycle 134: month-scoped treatments fetch for the Log
// calendar's day markers.
//
// Fetches the treatments covering the visible month grid (the full
// 35/42-day window + a 1-day buffer) ONCE per displayed month via the
// existing `GET /api/treatments` (`hours` / `before` / `limit` — the same
// windowing the day-stepper and the retired cycle-31 calendar used), then
// buckets by local `YYYY-MM-DD` with `localDayKey`. Refetches only when
// the displayed (year, month) changes — never per render. No new endpoint
// or query param.

import { useEffect, useState } from "react";

import * as treatmentsApi from "../../api/treatments";
import { localDayKey } from "../../lib/glucose";
import type { Treatment } from "../../lib/types";
import { buildMonthGrid, endOfLocalDayIso } from "./monthGrid";

export interface MonthTreatments {
  /** local `YYYY-MM-DD` → that day's treatments. */
  byDay: Map<string, Treatment[]>;
  loading: boolean;
  error: string | null;
}

export function useMonthTreatments(
  year: number,
  month: number,
): MonthTreatments {
  const [byDay, setByDay] = useState<Map<string, Treatment[]>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const { cells } = buildMonthGrid(year, month);
    const before = endOfLocalDayIso(cells[cells.length - 1]!);
    // The window must span the whole grid; +1 day of buffer before the
    // first cell. Well within the server's 90-day `hours` cap.
    const hours = (cells.length + 1) * 24;
    setLoading(true);
    void (async () => {
      const r = await treatmentsApi.list({ hours, before, limit: 5000 });
      if (cancelled) return;
      setLoading(false);
      if (r.ok) {
        const m = new Map<string, Treatment[]>();
        for (const t of r.data.treatments) {
          const k = localDayKey(t.timestamp);
          const arr = m.get(k);
          if (arr) arr.push(t);
          else m.set(k, [t]);
        }
        setByDay(m);
        setError(null);
      } else {
        setError(r.error.message);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Keyed on the displayed month only — one fetch per month change.
  }, [year, month]);

  return { byDay, loading, error };
}
